/**
 * Единая таксономия категорий: витрина (CatalogPage), админ-«полки», подсказки.
 * Ключ ветки после нормализации совпадает с тем, как группируется каталог для клиента.
 */
export const CANONICAL_CATEGORY_ORDER = [
  'Жалюзи',
  'Лифт-система',
  'Маркиза',
  'Пергола',
  'Плиссе',
  'Раздвижные электрокарнизы',
  'Римские шторы',
  'Рулонные шторы',
  'Японские шторы',
  'ZIP-система',
  'Австрийские шторы',
  'Моторы',
  'Умный дом',
  'Раздвижные карнизы',  // Temporary duplicate for migration
] as const;

export type CanonicalCategory = (typeof CANONICAL_CATEGORY_ORDER)[number];

/** Латиница/slug из CRM → подпись ветки как на витрине */
export const CATEGORY_ALIAS: Record<string, string> = {
  'elektrokarnizy dlya zhalyuzi': 'Раздвижные электрокарнизы',
  'elektrokarnizy dlya plisse': 'Плиссе',
  elektrodvigateli: 'Лифт-система',
  'pulty i klavishi': 'Пульты и аксессуары',
  'razdvizhnye elektrokarnizy': 'Раздвижные электрокарнизы',
  'rimskie elektrokarnizy': 'Римские шторы',
  'rulonnye elektrokarnizy': 'Рулонные шторы',
  'ulichnye pergoly': 'Пергола',
  'zip sistemy': 'ZIP-система',
  markizy: 'Маркиза',
  markiza: 'Маркиза',
  'umnyy dom': 'Умный дом',
  'smart home': 'Умный дом',
  'tuya': 'Умный дом',
  'mihome': 'Умный дом',
  'zigbee': 'Умный дом',
};

/** Иконки для категорий (emoji или lucide icon name) */
export const CATEGORY_ICON: Record<string, string> = {
  'Жалюзи': '🪟',
  'Лифт-система': '⬆️',
  'Маркиза': '⛱️',
  'Пергола': '🏛️',
  'Плиссе': '🪗',
  'Раздвижные электрокарнизы': '🚪',
  'Римские шторы': '🏛️',
  'Рулонные шторы': '📜',
  'Японские шторы': '🇯🇵',
  'ZIP-система': '🤐',
  'Австрийские шторы': '🇦🇹',
  'Моторы': '⚙️',
  'Умный дом': '🏠',
  'Раздвижные карнизы': '🚪',
};

/** Цвета для категорий (tailwind или hex) */
export const CATEGORY_COLOR: Record<string, string> = {
  'Жалюзи': '#63B3ED',
  'Лифт-система': '#68D391',
  'Маркиза': '#F6AD55',
  'Пергола': '#9F7AEA',
  'Плиссе': '#F687B3',
  'Раздвижные электрокарнизы': '#4FD1C5',
  'Римские шторы': '#E53E3E',
  'Рулонные шторы': '#3182CE',
  'Японские шторы': '#D69E2E',
  'ZIP-система': '#805AD5',
  'Австрийские шторы': '#38B2AC',
  'Моторы': '#718096',
  'Умный дом': '#38A169',
  'Раздвижные карнизы': '#4FD1C5',
};

export function hasCyrillic(text: string): boolean {
  return /[А-Яа-яЁё]/.test(text);
}

/** Как для табов витрины: alias + исходная строка если уже по-русски */
export function normalizeCatalogCategoryLabel(raw: string): string {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  const key = trimmed.toLowerCase();
  if (CATEGORY_ALIAS[key]) return CATEGORY_ALIAS[key];
  return trimmed;
}

/**
 * Ветка для админских «полок»: та же канонизация что на витрине; без совпадения — сырая строка или «без категории».
 */
export function adminShelfCategoryKey(rawCategory: string): string {
  const canon = normalizeCatalogCategoryLabel(rawCategory);
  if (canon) return canon;
  const t = (rawCategory || '').trim();
  return t || '— без категории';
}

/** Порядок веток: как CANONICAL_CATEGORY_ORDER, затем прочие по алфавиту */
export function compareAdminShelfCategories(a: string, b: string): number {
  const ia = CANONICAL_CATEGORY_ORDER.indexOf(a as CanonicalCategory);
  const ib = CANONICAL_CATEGORY_ORDER.indexOf(b as CanonicalCategory);
  const aKnown = ia !== -1;
  const bKnown = ib !== -1;
  if (aKnown && bKnown && ia !== ib) return ia - ib;
  if (aKnown && !bKnown) return -1;
  if (!aKnown && bKnown) return 1;
  return a.localeCompare(b, 'ru');
}

export function isCanonicalShelfCategory(displayCategory: string): boolean {
  return (CANONICAL_CATEGORY_ORDER as readonly string[]).includes(displayCategory);
}
